import { createContext, useContext, useState, useRef, useCallback } from 'react'
import { useHistory } from 'react-router'
import { toast } from 'atoms/Toast'
import { TranslationMap, TranslationMapLoaderEnum } from 'common/types/kraken-core/TranslationMap'
import {
  useImportTranslationMap,
  useCreateTranslationMap,
  useValidateImportTranslationMap,
} from '../api'
import { validateFields } from 'common/helpers/kraken-core/translationMaps.helper'

export type ImportMode = 'create' | 'import'
export type ImportStep = 'upload-step' | 'verify-step' | 'finish-step'
export type ViewMode = 'simple' | 'advanced'

export interface ImportModalContextProps {
  // Modal State
  isOpen: boolean
  mode: ImportMode
  currentStep: ImportStep
  importedData: TranslationMap | null
  viewMode: ViewMode
  validations: string | undefined

  // Loading States
  isImporting: boolean
  isCreating: boolean
  isValidating: boolean
  importError: any
  createError: any

  // Success Data
  importResult: any
  createResult: any

  // Actions
  openModal: (mode: ImportMode) => void
  closeModal: () => void
  setCurrentStep: (step: ImportStep) => void
  setViewMode: (mode: ViewMode) => void
  handleFileUpload: (acceptedFiles: File[]) => void
  validateImportData: () => Promise<boolean>
  handleImport: () => void
  onUpdate: (translationMap: TranslationMap) => void
  navigateToCreated: (id: string) => void
}

const ImportModalContext = createContext<ImportModalContextProps | undefined>(undefined)

export interface ImportModalProviderProps {
  children: React.ReactNode
}

export function ImportModalProvider({ children }: ImportModalProviderProps) {
  const history = useHistory()

  // Modal State
  const [isOpen, setIsOpen] = useState(false)
  const [mode, setMode] = useState<ImportMode>('import')
  const [currentStep, setCurrentStep] = useState<ImportStep>('upload-step')
  const [importedData, setImportedData] = useState<TranslationMap | null>(null)
  const [viewMode, setViewMode] = useState<ViewMode>('simple')
  const [validations, setValidations] = useState<string>()

  // Refs for stable data
  const loadedData = useRef<TranslationMap | null>(null)

  // API hooks
  const {
    mutate: importMutate,
    isLoading: isImporting,
    error: importError,
    data: importResult,
  } = useImportTranslationMap()

  const {
    mutate: createMutate,
    isLoading: isCreating,
    error: createError,
    data: createResult,
  } = useCreateTranslationMap()

  const { mutateAsync: validateAsync, isLoading: isValidating } = useValidateImportTranslationMap()

  // Actions
  const openModal = useCallback((newMode: ImportMode) => {
    console.log('ImportModalContext openModal called with mode:', newMode)
    setMode(newMode)
    setCurrentStep('upload-step')
    setIsOpen(true)

    if (newMode === 'create') {
      // Only initialize if we don't already have data
      setImportedData(prevData => {
        console.log('ImportModalContext setImportedData called with prevData:', prevData)
        if (!prevData) {
          console.log('ImportModalContext initializing new data')
          return {
            name: '',
            path: '',
            loader: TranslationMapLoaderEnum.RUNTIME_DATABASE_LOADER,
            output_reuse_original_message: true,
            output_content_type: 'application/json',
            output_encoding: 'UTF-8',
            output_name_pattern: 'payload.json',
            input_content_type: 'application/json',
            input_encoding: 'UTF-8',
            input_data_model: '',
            output_data_model: '',
            encoded_implementation: '# Your code goes here',
            settings_data_schema: {},
            output_headers: {},
            use_when_direction: 'inbound',
            use_when_facts: {},
          } as TranslationMap
        }
        console.log('ImportModalContext preserving existing data')
        return prevData
      })
    } else {
      setImportedData(null)
    }
  }, [])

  const closeModal = useCallback(() => {
    setImportedData(null)
    setCurrentStep('upload-step')
    setViewMode('simple')
    setValidations(undefined)
    loadedData.current = null
    setIsOpen(false)
  }, [])

  const handleFileUpload = useCallback((acceptedFiles: File[]) => {
    acceptedFiles.forEach((file: File) => {
      const reader = new FileReader()

      reader.onerror = () => {
        toast.error('There are some problems in your file, please review.')
      }

      reader.onload = () => {
        try {
          const binaryStr = reader.result
          const data = JSON.parse(binaryStr?.toString() || '{}')
          loadedData.current = data
          setValidations(undefined)
          setImportedData(data)
        } catch (error) {
          toast.error('There are some problems in your file, please review.')
        }
      }

      reader.readAsText(file)
    })
  }, [])

  const validateImportData = useCallback(async (): Promise<boolean> => {
    const data = loadedData.current || importedData

    if (!data) {
      setValidations('No data was loaded')
      return false
    }

    try {
      const result = await validateAsync(data)
      if (!result?.valid) {
        const flat = Object.entries(result?.errors || {})
          .map(([field, msgs]) => `${field}: ${(msgs as string[]).join(' ')}`)
          .join('\n')
        setValidations(`This map references objects that don't exist in this environment:\n${flat}`)
        return false
      }
      setValidations(undefined)
      return true
    } catch (error) {
      setValidations(`Could not validate the file: ${error}`)
      return false
    }
  }, [importedData, validateAsync])

  const onUpdate = useCallback((updated: TranslationMap) => {
    console.log('ImportModalContext onUpdate called with:', updated)
    loadedData.current = updated
    setImportedData(prevData => {
      console.log('ImportModalContext setImportedData functional update with prevData:', prevData)
      console.log('ImportModalContext setImportedData functional update with updated:', updated)
      return updated
    })
  }, [])

  const handleImport = useCallback(() => {
    // Use loadedData.current if available, otherwise fall back to importedData
    const data = loadedData.current || importedData

    if (!data) {
      toast.error('No data was loaded')
      return
    }
    const { rawUseWhen, rawOutputHeaders, ...rest } = data as any

    let translationMapUseWhen
    let translationMapOutputHeaders

    try {
      translationMapUseWhen = rawUseWhen ? JSON.parse(rawUseWhen) : rest.use_when_facts
      translationMapOutputHeaders = rawOutputHeaders
        ? JSON.parse(rawOutputHeaders)
        : rest.output_headers
      setValidations(undefined)
      validateFields(rest as any)

      const finalData = {
        ...rest,
        use_when_facts: translationMapUseWhen,
        output_headers: translationMapOutputHeaders,
        implementation_type: rest.implementation_type || 'python',
      }

      if (mode === 'create') {
        // For create mode, exclude id and timestamp fields
        const { id, created_at, updated_at, deleted_at, ...createData } = finalData
        createMutate(createData)
      } else {
        // For import mode, use the full data
        importMutate(finalData)
      }

      setCurrentStep('finish-step')
    } catch (error) {
      setValidations(`There are some problems in your form: ${error}`)
    }
  }, [mode, importMutate, createMutate, importedData])

  const navigateToCreated = useCallback(
    (id: string) => {
      history.push(`/translation-maps/${id}`)
    },
    [history]
  )

  const contextValue: ImportModalContextProps = {
    // Modal State
    isOpen,
    mode,
    currentStep,
    importedData,
    viewMode,
    validations,

    // Loading States
    isImporting,
    isCreating,
    isValidating,
    importError,
    createError,

    // Success Data
    importResult,
    createResult,

    // Actions
    openModal,
    closeModal,
    setCurrentStep,
    setViewMode,
    handleFileUpload,
    validateImportData,
    handleImport,
    onUpdate,
    navigateToCreated,
  }

  return <ImportModalContext.Provider value={contextValue}>{children}</ImportModalContext.Provider>
}

export function useImportModalContext() {
  const context = useContext(ImportModalContext)
  if (context === undefined) {
    throw new Error('useImportModalContext must be used within an ImportModalProvider')
  }
  return context
}
